/* eslint-disable eqeqeq */
/* eslint-disable camelcase */
import { Box, IconButton, Typography, makeStyles } from '@material-ui/core'
import React, { useEffect, useState } from 'react'
import colors from '../../../assets/colors'
import {
  addMonths,
  endOfDay,
  endOfMonth,
  endOfWeek,
  format,
  isAfter,
  isValid,
  parseISO,
  startOfDay,
  startOfMonth,
  startOfWeek,
  subMonths,
} from 'date-fns'
import Icon from '@mdi/react'
import { mdiMenuLeft, mdiMenuRight } from '@mdi/js'
import { ptBR } from 'date-fns/locale'
import { KeyboardDatePicker } from '@material-ui/pickers'

const useStyles = makeStyles({
  box: {
    display: 'flex',
    flexDirection: 'row',
  },
  period: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    padding: '4px 12px',
    border: `1px solid ${colors.graylight}`,
    transition: 'all .4s ease-in-out',
    '&:hover': {
      opacity: '.7',
    },
    cursor: 'pointer',
  },
  borderRight: {
    borderRadius: '0px 5px 5px 0',
  },
  borderLeft: {
    borderRadius: '5px 0 0 5px',
  },
  seletedPeriod: {
    color: '#FFF',
    backgroundColor: colors.primary,
  },
  filterBox: {
    display: 'flex',
    gap: '8px',
    alignItems: 'center',
    justifyContent: 'flex-start',
    fontSize: '14px',
    lineHeight: '14px',
    color: colors.gray,
    margin: '0 4px',
  },
  periodFilter: {
    color: colors.primary,
    display: 'flex',
    minWidth: '210px',
    padding: '4px',
    alignItems: 'center',
    justifyContent: 'space-between',
    border: `1px solid ${colors.graylight}`,
    borderRadius: '5px',
    fontSize: '14px',
    lineHeight: '14px',
  },
  customPeriodInput: {
    width: '165px',
    margin: 0,
    '& .MuiOutlinedInput-adornedEnd': {
      paddingRight: '4px',
    },
  },
  periodFilterBtn: {
    padding: 0,
    transition: 'all .4s',
    color: colors.primary,
    '&:hover': {
      opacity: '.7',
      backgroundColor: '#FFF',
    },
  },
})

export const PERIODS = ['custom', 'month', 'week', 'day']

const formatDate = value => format(value, 'yyyy-MM-dd')

export const getPeriodRange = (period, base = new Date()) => {
  if (period == 'week') {
    return {
      date_start: formatDate(startOfWeek(base)),
      date_end: formatDate(endOfWeek(base)),
    }
  }

  if (period == 'month' || period == 'custom') {
    return {
      date_start: formatDate(startOfMonth(base)),
      date_end: formatDate(endOfMonth(base)),
    }
  }

  return {
    date_start: formatDate(startOfDay(base)),
    date_end: formatDate(endOfDay(base)),
  }
}

const PeriodFilter = ({ filters, setFilters }) => {
  const classes = useStyles()

  const period = PERIODS.includes(filters?.period) ? filters.period : 'day'
  const dateStart = parseISO(filters?.date_start)
  const dateEnd = parseISO(filters?.date_end)
  const monthDate = isValid(dateStart) ? dateStart : new Date()

  // estado local para permitir digitação de datas incompletas
  const [customRange, setCustomRange] = useState({
    start: dateStart,
    end: dateEnd,
  })

  useEffect(() => {
    setCustomRange({
      start: parseISO(filters?.date_start),
      end: parseISO(filters?.date_end),
    })
  }, [filters?.date_start, filters?.date_end])

  const handleSelect = selected => {
    if (selected == period) return

    // período personalizado parte das datas que já estão aplicadas
    const range =
      selected == 'custom' && isValid(dateStart) && isValid(dateEnd)
        ? { date_start: filters.date_start, date_end: filters.date_end }
        : getPeriodRange(selected)

    setFilters({ ...filters, period: selected, ...range })
  }

  const handleChangeMonth = type => {
    const base =
      type == 'prev' ? subMonths(monthDate, 1) : addMonths(monthDate, 1)

    setFilters({ ...filters, ...getPeriodRange('month', base) })
  }

  const handleChangeCustomRange = (field, value) => {
    const range = { ...customRange, [field]: value }
    setCustomRange(range)

    const { start, end } = range

    if (!isValid(start) || !isValid(end) || isAfter(start, end)) return

    setFilters({
      ...filters,
      date_start: formatDate(start),
      date_end: formatDate(end),
    })
  }

  return (
    <Box className={classes.box}>
      {/* só aparece quando o filtro é mensal */}
      {period == 'month' && (
        <Box className={classes.filterBox}>
          <Box className={classes.periodFilter}>
            <IconButton
              onClick={() => {
                handleChangeMonth('prev')
              }}
              className={classes.periodFilterBtn}
            >
              <Icon path={mdiMenuLeft} size={0.7} />
            </IconButton>

            <Typography style={{ textTransform: 'capitalize' }}>
              {format(monthDate, "MMMM '-' yyyy", { locale: ptBR })}
            </Typography>

            <IconButton
              onClick={() => {
                handleChangeMonth('next')
              }}
              className={classes.periodFilterBtn}
            >
              <Icon path={mdiMenuRight} size={0.7} />
            </IconButton>
          </Box>
        </Box>
      )}

      {/* só aparece quando o filtro é por período personalizado */}
      {period == 'custom' && (
        <Box className={classes.filterBox}>
          <KeyboardDatePicker
            value={customRange.start}
            label="Data inicial"
            invalidDateMessage="Data inválida"
            maxDate={customRange.end}
            maxDateMessage="Maior que a data final"
            autoOk
            size="small"
            inputVariant="outlined"
            format="dd/MM/yyyy"
            KeyboardButtonProps={{ size: 'small' }}
            className={classes.customPeriodInput}
            onChange={value => handleChangeCustomRange('start', value)}
          />
          <KeyboardDatePicker
            value={customRange.end}
            label="Data final"
            invalidDateMessage="Data inválida"
            minDate={customRange.start}
            minDateMessage="Menor que a data inicial"
            autoOk
            size="small"
            inputVariant="outlined"
            format="dd/MM/yyyy"
            KeyboardButtonProps={{ size: 'small' }}
            className={classes.customPeriodInput}
            onChange={value => handleChangeCustomRange('end', value)}
          />
        </Box>
      )}

      <Box
        className={[
          classes.period,
          classes.borderLeft,
          period == 'custom' ? classes.seletedPeriod : '',
        ]}
        onClick={() => handleSelect('custom')}
      >
        Período
      </Box>
      <Box
        className={[
          classes.period,
          period == 'month' ? classes.seletedPeriod : '',
        ]}
        onClick={() => handleSelect('month')}
      >
        Mês
      </Box>
      <Box
        className={[
          classes.period,
          period == 'week' ? classes.seletedPeriod : '',
        ]}
        onClick={() => handleSelect('week')}
      >
        Semana
      </Box>
      <Box
        className={[
          classes.period,
          classes.borderRight,
          period == 'day' ? classes.seletedPeriod : '',
        ]}
        onClick={() => handleSelect('day')}
      >
        Dia
      </Box>
    </Box>
  )
}

export default PeriodFilter
